import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Heart, MapPin, Music, Plane } from "lucide-react";
import { CITY, FAMILY, PHOTOS } from "@/data/content";
import { Kicker, Reveal, Star } from "./Reveal";

const PILLS = [
  { icon: Music, t: "Músicas autorais" },
  { icon: Plane, t: "Viagens & passeios" },
  { icon: Heart, t: "Rotina em família" },
];

const Members = () => (
  <ul className="mt-8 grid gap-3 sm:grid-cols-3">
    {FAMILY.map((m, i) => (
      <Reveal key={m.key} delay={0.1 + i * 0.08}>
        <li data-testid={`family-member-${m.key}`} className="h-full rounded-2xl border-2 border-[var(--ink)]/10 bg-[var(--cream)] p-4">
          <span className="block h-1.5 w-8 rounded-full" style={{ background: m.color }} />
          <span className="font-display mt-2 block text-2xl font-extrabold leading-none text-[var(--ink)]">{m.name}</span>
          <span className="mt-1 block text-sm font-medium text-[var(--ink)]/75">{m.role}</span>
        </li>
      </Reveal>
    ))}
  </ul>
);

const Photos = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [30, -10]);
  return (
    <div ref={ref} className="relative pb-24 sm:pb-28">
      <motion.figure
        initial={{ opacity: 0, y: 50, rotate: 0 }}
        whileInView={{ opacity: 1, y: 0, rotate: -2 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ type: "spring", stiffness: 90, damping: 15 }}
        data-testid="family-photo-main"
        className="rounded-[1.6rem] bg-white p-3 shadow-[0_30px_60px_-25px_rgba(27,27,51,0.5)]"
      >
        <div className="aspect-[16/10] overflow-hidden rounded-2xl bg-[#f1f1f1]">
          <img src={PHOTOS.familia} alt="Diego, Dani e Pedro" className="h-full w-full object-cover object-[50%_20%]" />
        </div>
        <figcaption className="font-display px-2 pt-2 text-lg font-bold text-[var(--ink)]">Diego, Dani & Pedro</figcaption>
      </motion.figure>
      <motion.figure
        style={{ y }}
        initial={{ opacity: 0, scale: 0.85 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, amount: 0.1 }}
        whileHover={{ rotate: 0, scale: 1.04 }}
        transition={{ type: "spring", stiffness: 120, damping: 14, delay: 0.15 }}
        data-testid="family-photo-pedro"
        className="absolute -bottom-8 right-0 w-[38%] rotate-[5deg] rounded-[1.4rem] bg-white p-2.5 shadow-[0_30px_50px_-20px_rgba(27,27,51,0.55)] sm:-right-2 lg:w-[30%]"
      >
        <div className="aspect-[3/4] overflow-hidden rounded-xl bg-[#f1f1f1]">
          <img src={PHOTOS.pedro} alt="Pedro" className="h-full w-full object-cover object-top" />
        </div>
        <figcaption className="font-display px-1 pt-1.5 text-base font-bold text-[var(--red)]">Pedro ★</figcaption>
      </motion.figure>
      <span
        data-testid="family-city-badge"
        className="absolute bottom-6 left-2 inline-flex -rotate-3 items-center gap-2 rounded-full bg-[var(--ink)] px-4 py-2 text-sm font-bold text-white shadow-lg"
      >
        <MapPin className="h-4 w-4 text-[var(--yellow)]" /> {CITY}
      </span>
      <Star className="floaty absolute -top-5 right-[30%] h-10 w-10" />
    </div>
  );
};

export const About = () => (
  <section id="sobre" className="felt relative overflow-hidden bg-white px-5 py-24 sm:px-8 sm:py-32" data-testid="about-family-section">
    <div className="mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-12">
      <Reveal className="lg:col-span-5">
        <Kicker className="text-[var(--ink)]/70">Sobre nós</Kicker>
        <h2 className="font-display mt-3 text-5xl font-extrabold leading-[0.95] text-[var(--ink)] sm:text-6xl">
          Uma família <span className="puffy puffy-green">de verdade</span>, criando juntos.
        </h2>
        <p className="mt-6 text-base text-[var(--ink)]/75 sm:text-lg">
          Somos a família Diego, Dani e Pedro, de Porto Alegre (RS). O papai Diego é publicitário e a mamãe Dani é administradora. Nossa paixão é criar memórias, músicas e entretenimento de valor para toda a família, com conteúdo seguro, autêntico e de alta qualidade.
        </p>
        <ul className="mt-6 flex flex-wrap gap-2">
          {PILLS.map(({ icon: Icon, t }) => (
            <li key={t} className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)]/10 bg-[var(--cream)] px-4 py-2 text-sm font-semibold">
              <Icon className="h-4 w-4 text-[var(--blue)]" /> {t}
            </li>
          ))}
        </ul>
        <Members />
      </Reveal>
      <div className="lg:col-span-7">
        <Photos />
      </div>
    </div>
  </section>
);
